(() => {
  'use strict';
  const config = window.Viebys || {};
  // Resolve fetch when the request starts so Shopify can observe cart mutations.
  const nativeCartFetch = (...args) => window.fetch(...args);
  const words = config.strings || {};
  const route = () => window.Shopify?.routes?.root || '/';
  const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const get = (scope, selector) => scope.querySelector(selector);
  const all = (scope, selector) => Array.from(scope.querySelectorAll(selector));
  async function cartRequest(endpoint, body) {
    const response = await nativeCartFetch(route() + endpoint, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      ...(body ? { body: JSON.stringify(body) } : {})
    });
    const result = await response.json();
    if (!response.ok || result.status >= 400) {
      throw new Error(result.description || words.cart_error || 'Le panier n’a pas été mis à jour.');
    }
    return result;
  }
  window.vbPackProperties = choice => {
    const label = choice.closest('[data-vb-product]')?.dataset.vbPropertyLabel || 'Cadeaux inclus';
    return choice.dataset.vbGifts ? { [label]: choice.dataset.vbGifts } : {};
  };
  function selectedOffer() {
    return get(document, '[data-vb-product] input[data-vb-variant]:checked');
  }
  function showSlide(product, index) {
    if (product._vbGallery) {
      product._vbGallery.goTo(Number(index));
      return;
    }
    all(product, '[data-slide]').forEach(item => { item.hidden = item.dataset.slide !== String(index); });
    all(product, '[data-show-slide]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.showSlide === String(index))));
  }
  function initGallery(product) {
    const stage = get(product, '.vb-gallery-stage');
    const slides = all(product, '[data-slide]');
    if (!stage || slides.length < 2) return () => {};
    const buttons = all(product, '[data-show-slide]');
    const listeners = new AbortController();
    const listen = (node, name, fn, options = {}) => node.addEventListener(name, fn, { ...options, signal: listeners.signal });
    let current = 0;
    let frame = 0;
    let drag = null;
    let ignoreClick = false;
    const update = index => {
      current = Math.max(0, Math.min(slides.length - 1, index));
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === current));
      buttons.forEach(button => button.setAttribute('aria-pressed', String(Number(button.dataset.showSlide) === current)));
    };
    const goTo = index => {
      const target = Math.max(0, Math.min(slides.length - 1, index));
      stage.scrollTo({ left: target * stage.clientWidth, behavior: reduceMotion() ? 'instant' : 'smooth' });
    };
    slides.forEach(slide => {
      slide.hidden = false;
      all(slide, 'img').forEach(img => { img.draggable = false; });
    });
    stage.classList.add('vb-gallery-scroll');
    stage.tabIndex = 0;
    stage.setAttribute('role', 'region');
    stage.setAttribute('aria-roledescription', 'carrousel');
    stage.setAttribute('aria-label', get(product, '.vb-thumbnails')?.getAttribute('aria-label') || 'Images du produit');
    product._vbGallery = { goTo };
    update(0);
    listen(stage, 'scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (stage.clientWidth) update(Math.round(stage.scrollLeft / stage.clientWidth));
      });
    }, { passive: true });
    buttons.forEach(button => listen(button, 'click', () => goTo(Number(button.dataset.showSlide))));
    listen(stage, 'keydown', event => {
      const destinations = { ArrowLeft: current - 1, ArrowRight: current + 1, Home: 0, End: slides.length - 1 };
      if (!(event.key in destinations)) return;
      event.preventDefault();
      goTo(destinations[event.key]);
    });
    // Touch and trackpad scrolling remain native; only mouse dragging is handled here.
    listen(stage, 'pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      drag = { id: event.pointerId, x: event.clientX, left: stage.scrollLeft, moved: false };
      ignoreClick = false;
      stage.setPointerCapture(event.pointerId);
    });
    listen(stage, 'pointermove', event => {
      if (!drag || drag.id !== event.pointerId) return;
      const delta = event.clientX - drag.x;
      if (!drag.moved && Math.abs(delta) < 5) return;
      drag.moved = true;
      stage.classList.add('is-dragging');
      stage.scrollLeft = drag.left - delta;
    });
    const finishDrag = event => {
      if (!drag || drag.id !== event.pointerId) return;
      const moved = drag.moved;
      drag = null;
      stage.classList.remove('is-dragging');
      if (stage.hasPointerCapture(event.pointerId)) stage.releasePointerCapture(event.pointerId);
      if (moved) {
        ignoreClick = true;
        goTo(Math.round(stage.scrollLeft / stage.clientWidth));
      }
    };
    listen(stage, 'pointerup', finishDrag);
    listen(stage, 'pointercancel', finishDrag);
    listen(stage, 'lostpointercapture', finishDrag);
    listen(stage, 'click', event => {
      if (!ignoreClick) return;
      ignoreClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, { capture: true });
    const resize = new ResizeObserver(() => {
      if (stage.clientWidth) stage.scrollTo({ left: current * stage.clientWidth, behavior: 'instant' });
    });
    resize.observe(stage);
    return () => {
      listeners.abort();
      resize.disconnect();
      cancelAnimationFrame(frame);
      delete product._vbGallery;
    };
  }
  function initProduct(product) {
    if (product.dataset.vbReady) return;
    product.dataset.vbReady = 'true';
    const destroyGallery = initGallery(product);
    product._vbDestroy = destroyGallery;
    const form = get(product, '.vb-product-form');
    if (!form) return;
    const status = get(product, '[data-vb-status]');
    const submit = get(form, 'button[type="submit"]');
    const sticky = get(product, '[data-vb-sticky]');
    let busy = false;
    function syncOffer(choice, updateUrl = true) {
      if (!choice) return;
      all(product, '.vb-pack').forEach(label => label.classList.toggle('is-selected', !!get(label, 'input:checked')));
      all(product, '[data-vb-price]').forEach(node => { node.textContent = choice.dataset.price; });
      all(product, '[data-vb-gift-panel]').forEach(panel => { panel.hidden = panel.dataset.vbGiftPanel !== choice.dataset.vbVariant; });
      submit.disabled = choice.disabled;
      if (sticky) get(sticky, 'button').disabled = choice.disabled;
      if (updateUrl) {
        const next = new URL(location.href);
        next.searchParams.set('variant', choice.dataset.vbVariant);
        history.replaceState(null, '', next);
      }
    }
    syncOffer(get(form, 'input[data-vb-variant]:checked'), false);
    form.addEventListener('change', event => {
      if (event.target.matches('input[data-vb-variant]')) syncOffer(event.target);
    });
    form.addEventListener('submit', async event => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy) return;
      const choice = get(form, 'input[data-vb-variant]:checked');
      if (!choice || choice.disabled) return;
      busy = true;
      submit.setAttribute('aria-busy', 'true');
      submit.disabled = true;
      if (sticky) get(sticky, 'button').disabled = true;
      status.textContent = words.adding;
      try {
        await cartRequest('cart/add.js', { items: [{ id: Number(choice.dataset.vbVariant), quantity: 1, properties: window.vbPackProperties(choice) }] });
        status.textContent = words.added;
        let giftError = '';
        try { await window.vbWelcome?.syncCart(); } catch(error) { giftError = error.message; }
        if (window.vbCart) await window.vbCart.open(submit);
        else location.assign(route() + 'cart');
        busy = false; submit.disabled = false; submit.removeAttribute('aria-busy');
        if (sticky) get(sticky, 'button').disabled = false;
        status.textContent = giftError || '';
      } catch (error) {
        status.textContent = error.message || words.cart_error;
        busy = false;
        submit.disabled = false;
        if (sticky) get(sticky, 'button').disabled = false;
        submit.removeAttribute('aria-busy');
      }
    }, true);
    if (sticky) {
      let frame = 0;
      const refreshSticky = () => {
        frame = 0;
        sticky.hidden = submit.getBoundingClientRect().bottom >= 0;
      };
      const scheduleSticky = () => {
        if (!frame) frame = requestAnimationFrame(refreshSticky);
      };
      window.addEventListener('scroll', scheduleSticky, { passive: true });
      window.addEventListener('resize', scheduleSticky);
      refreshSticky();
      product._vbDestroy = () => {
        destroyGallery();
        cancelAnimationFrame(frame);
        window.removeEventListener('scroll', scheduleSticky);
        window.removeEventListener('resize', scheduleSticky);
      };
    }
  }
  function initHeader(header) {
    if (header._vbHeaderDestroy) return;
    const section = header.closest('.shopify-section');
    if (!section) return;
    let frame = 0;
    let lastY = Math.max(0, window.scrollY);
    let travel = 0;
    let direction = 0;
    section.classList.add('vb-sticky-header');
    const show = () => section.classList.remove('vb-sticky-header--hidden');
    const update = () => {
      frame = 0;
      const maxY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      const y = Math.min(maxY, Math.max(0, window.scrollY));
      const delta = y - lastY;
      lastY = y;
      if (y <= section.offsetHeight || header.querySelector('details[open]') || header.querySelector(':focus-visible')) {
        travel = 0;
        direction = 0;
        show();
        return;
      }
      const nextDirection = Math.sign(delta);
      if (!nextDirection) return;
      travel = nextDirection === direction ? travel + Math.abs(delta) : Math.abs(delta);
      direction = nextDirection;
      if (travel >= 8) {
        section.classList.toggle('vb-sticky-header--hidden', direction > 0);
        travel = 0;
      }
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const reveal = () => { show(); travel = 0; };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    header.addEventListener('focusin', reveal);
    header.addEventListener('toggle', schedule, true);
    header._vbHeaderDestroy = () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      header.removeEventListener('focusin', reveal);
      header.removeEventListener('toggle', schedule, true);
      section.classList.remove('vb-sticky-header', 'vb-sticky-header--hidden');
      delete header._vbHeaderDestroy;
    };
  }
  function boot(scope = document) {
    all(scope, '.vb-header').forEach(initHeader);
    all(scope, '[data-vb-product]').forEach(initProduct);

    syncGiftLinks();
  }
  function syncGiftLinks(excluded) {
    const welcome = all(document, '[data-vb-welcome]').find(node => !excluded?.contains(node) && !node.closest('[hidden]'));
    all(document, '[data-vb-open-gift]').forEach(button => { button.hidden = !welcome; });
  }
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => {
    all(event.target, '.vb-header').forEach(node => node._vbHeaderDestroy?.());
    all(event.target, '[data-vb-product]').forEach(node => node._vbDestroy?.());
    all(event.target, '[data-vb-welcome]').forEach(node => node._vbDestroy?.());
    syncGiftLinks(event.target);
  });
  document.addEventListener('shopify:block:select', event => {
    const slide = event.target.closest('[data-slide]');
    if (slide) showSlide(slide.closest('[data-vb-product]'), slide.dataset.slide);
    event.target.closest('details')?.setAttribute('open', '');
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
})();

